/**
 * "O Grupie" / "The Group" — the mock-up's group page: a vertical video block,
 * the group narrative, its pillars and a trade-fair photo strip.
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import { applyPageSeo } from "../core/seo.js";
import { contentSeed } from "../data/seed.js";
import { videoBlock } from "../components/video.js";
import { eyebrow } from "../components/common.js";

// Synchronous: the page's content comes from the shared seed, not the gateway.
export function aboutView(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	const seed = contentSeed(ctx.locale());

	applyPageSeo(ctx, {
		title: `${copy.nav.about} — ${copy.brand.name}`,
		description: copy.about.lead,
	});

	const video = videoBlock(ctx, {
		source: "grupa",
		poster: "team-stoisko",
		id: "mb-video-group",
		label: copy.about.title,
	});

	const view = el("div", { class: "mb-view mb-view--about" });

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--glow" },
			el(
				"div",
				{ class: "mb-section__inner mb-videosection__grid" },
				el(
					"div",
					{ class: "mb-videosection__text" },
					eyebrow(copy.about.eyebrow),
					el("h1", { class: "mb-videosection__title" }, copy.about.title),
					el("p", { class: "mb-videosection__lead" }, copy.about.lead),
					el("div", { class: "mb-videosection__actions" }, video.controls),
				),
				video.frame,
			),
		),
	);

	const pillars = el("div", { class: "mb-grid mb-grid--2 mb-grid--tight" });
	for (const pillar of seed.pillars) {
		pillars.appendChild(
			el(
				"article",
				{ class: "mb-pillar" },
				el("h3", { class: "mb-pillar__title" }, pillar.title),
				el("p", { class: "mb-pillar__desc" }, pillar.desc),
			),
		);
	}

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner mb-twocol mb-twocol--start" },
				el(
					"div",
					null,
					el("h2", { class: "mb-blocktitle mb-blocktitle--lead" }, copy.about.bodyTitle),
					...copy.about.bodyParagraphs.map((paragraph) =>
						el("p", { class: "mb-prose" }, paragraph),
					),
				),
				el("div", null, el("h2", { class: "mb-sronly" }, copy.about.pillarsTitle), pillars),
			),
		),
	);

	const isEn = ctx.locale() === "en";
	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--paper" },
			el(
				"div",
				{ class: "mb-section__inner mb-grid mb-grid--3 mb-grid--tight" },
				el("img", {
					class: "mb-photo",
					src: "/static/team-stoisko.webp",
					alt: isEn ? "The MB Group stand" : "Stoisko MB Group",
					loading: "lazy",
					decoding: "async",
				}),
				el("img", {
					class: "mb-photo",
					src: "/static/handshake.webp",
					alt: isEn ? "MB Group partnerships" : "Partnerstwa MB Group",
					loading: "lazy",
					decoding: "async",
				}),
				el("img", {
					class: "mb-photo",
					src: "/static/p6.webp",
					alt: isEn ? "The MB Group team" : "Zespół MB Group",
					loading: "lazy",
					decoding: "async",
				}),
			),
		),
	);

	return view;
}
